import React from "react";
import { Lightbulb, CheckCircle2, Clock, Shield, Award } from "lucide-react";
import { motion } from "framer-motion";

const AboutUs = () => {
    const companyInfo = {
        description: [
            { text: "With over " },
            { text: "10 years of experience", bold: true },
            { text: ", ZovoX Technologies delivers " },
            { text: "innovative IT solutions", bold: true },
            { text: " tailored to your unique business needs. Our team of " },
            { text: "certified experts", bold: true },
            { text: " blends technical excellence with strategic insight, helping you navigate the digital landscape and achieve " },
            { text: "sustainable, long-term growth", bold: true },
            { text: "." }
        ]
    };

    return (
        <section className="relative px-4 py-16 xs:px-4 sm:px-4 md:py-20 3xl:py-32 overflow-hidden">
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 -z-10"></div>

            {/* Blobs */}
            <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/30 rounded-full blur-3xl animate-pulse -z-10"></div>
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse -z-10"></div>

            {/* Text Section */}
            <div className="max-w-4xl 3xl:max-w-5xl mx-auto text-center mb-12 3xl:mb-20">
                <div className="inline-flex items-center justify-center w-20 h-20 3xl:w-24 3xl:h-24 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-6 shadow-xl shadow-blue-500/20 backdrop-blur-sm">
                    <Lightbulb className="w-10 h-10 3xl:w-12 3xl:h-12 text-white" strokeWidth={2} />
                </div>

                <h2 className="text-3xl sm:text-4xl md:text-5xl 3xl:text-6xl font-bold mb-6 3xl:mb-8 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-900 bg-clip-text text-transparent leading-normal py-1">
                    About Our Company
                </h2>

                <p className="text-sm sm:text-base md:text-lg lg:text-xl 3xl:text-2xl text-slate-600 leading-loose">
                    {companyInfo.description.map((part, i) =>
                        part.bold ? <b key={i}>{part.text}</b> : part.text
                    )}
                </p>
            </div>

            {/* Cards Grid — updated width like ServiceCards */}
            <div className="container mx-auto 3xl:max-w-[1800px]">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8 3xl:gap-12 mt-12 3xl:mt-20">

                    {/* Card 1 - Certified Experts */}
                    <motion.div
                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                        whileHover="hover"
                    >
                        <div className="flex flex-col items-center text-center">
                            <motion.div
                                className="w-14 h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30"
                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                transition={{ duration: 0.5 }}
                            >
                                <CheckCircle2 className="w-7 h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                            </motion.div>
                            <h3 className="font-semibold text-slate-900 text-lg 3xl:text-xl mb-2">Certified Experts</h3>
                            <p className="text-sm 3xl:text-base text-slate-600">Industry-leading professionals driving impactful solutions.</p>
                        </div>
                    </motion.div>

                    {/* Card 2 - 24/7 Support */}
                    <motion.div
                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                        whileHover="hover"
                    >
                        <div className="flex flex-col items-center text-center">
                            <motion.div
                                className="w-14 h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30"
                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                transition={{ duration: 0.5 }}
                            >
                                <Clock className="w-7 h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                            </motion.div>
                            <h3 className="font-semibold text-slate-900 text-lg 3xl:text-xl mb-2">24/7 Support</h3>
                            <p className="text-sm 3xl:text-base text-slate-600">Always available to assist you.</p>
                        </div>
                    </motion.div>

                    {/* Card 3 - Secure Solutions */}
                    <motion.div
                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                        whileHover="hover"
                    >
                        <div className="flex flex-col items-center text-center">
                            <motion.div
                                className="w-14 h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center mb-4 shadow-lg shadow-purple-500/30"
                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                transition={{ duration: 0.5 }}
                            >
                                <Shield className="w-7 h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                            </motion.div>
                            <h3 className="font-semibold text-slate-900 text-lg 3xl:text-xl mb-2">Secure Solutions</h3>
                            <p className="text-sm 3xl:text-base text-slate-600">Enterprise-grade security for your business.</p>
                        </div>
                    </motion.div>

                    {/* Card 4 - Award-Winning */}
                    <motion.div
                        className="group relative backdrop-blur-lg bg-white/60 border border-white/20 rounded-2xl p-6 3xl:p-8 shadow-xl hover:shadow-2xl hover:bg-white/80 transition-all duration-300 hover:-translate-y-2"
                        whileHover="hover"
                    >
                        <div className="flex flex-col items-center text-center">
                            <motion.div
                                className="w-14 h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/30"
                                variants={{ hover: { scale: 1.1, rotate: [0, -10, 10, -10, 0] } }}
                                transition={{ duration: 0.5 }}
                            >
                                <Award className="w-7 h-7 3xl:w-8 3xl:h-8 text-white" strokeWidth={2.5} />
                            </motion.div>
                            <h3 className="font-semibold text-slate-900 text-lg 3xl:text-xl mb-2">Award-Winning</h3>
                            <p className="text-sm 3xl:text-base text-slate-600">Recognized for excellence and innovation.</p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
};

export default AboutUs;
